import { Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  MoneyInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TableScroll,
} from '@mlm/ui';
import {
  cancelPayout,
  getPayoutMethods,
  getWallet,
  listPayouts,
  previewPayout,
  requestPayout,
  apiErrorMessage,
} from '../lib/api';
import { useHouseMoneyHidden } from '../lib/use-house-money';
import { QueryError } from '../components/query-error';

/** Codes this page explains next to the button, so the global toast stays quiet. */
const INLINE_ERRORS = [
  'amount_must_be_positive',
  'insufficient_balance',
  'payout_net_non_positive',
  'payout_destination_required',
  'unknown_payout_method',
];

export function PayoutsPage() {
  // A HOUSE creator has no wallet to show; the nav hides these entries, this
  // is the deep-link backstop.
  const hideMoney = useHouseMoneyHidden();
  const qc = useQueryClient();
  const walletQ = useQuery({ queryKey: ['creator-wallet'], queryFn: getWallet });
  const methodsQ = useQuery({
    queryKey: ['creator-payout-methods'],
    queryFn: getPayoutMethods,
  });
  const requestsQ = useQuery({
    queryKey: ['creator-payouts'],
    queryFn: listPayouts,
  });
  const wallet = walletQ.data;
  const methods = methodsQ.data ?? [];
  const requests = requestsQ.data ?? [];
  const methodLabel = (code: string) => methods.find((m) => m.code === code)?.label ?? code;

  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('');
  const [account, setAccount] = useState('');
  const [preview, setPreview] = useState<Awaited<ReturnType<typeof previewPayout>> | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Priced once typing pauses, not on every keystroke.
  useEffect(() => {
    if (!amount || !method || Number(amount) <= 0) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      previewPayout(amount, method)
        .then((p) => !cancelled && setPreview(p))
        .catch(() => !cancelled && setPreview(null));
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [amount, method]);

  const submit = useMutation({
    meta: { successMessage: 'Payout requested.', silentErrorCodes: INLINE_ERRORS },
    mutationFn: () => requestPayout({ amount, method, payload: { account: account.trim() } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['creator-payouts'] });
      qc.invalidateQueries({ queryKey: ['creator-wallet'] });
      setAmount('');
      setAccount('');
      setError(null);
    },
    onError: (e: unknown) => setError(apiErrorMessage(e, 'The request did not go through.')),
  });

  const cancel = useMutation({
    meta: { successMessage: 'Payout cancelled.' },
    mutationFn: (id: string) => cancelPayout(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['creator-payouts'] });
      qc.invalidateQueries({ queryKey: ['creator-wallet'] });
    },
  });

  const selectedMethod = methods.find((m) => m.code === method);

  if (hideMoney) return <Navigate to="/" replace />;

  return (
    <div className="max-w-3xl space-y-4">
      <h1 className="text-2xl font-semibold">Payouts</h1>

      <Card>
        <CardHeader>
          <CardTitle>Request a cash-out</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {walletQ.isError ? (
            <QueryError
              what="your balance"
              error={walletQ.error}
              onRetry={() => void walletQ.refetch()}
            />
          ) : (
            <p className="text-sm text-muted-foreground">
              Available: {wallet ? `₱${Number(wallet.available).toFixed(2)}` : '…'}
            </p>
          )}
          {methodsQ.isError ? (
            <QueryError
              what="the payout methods"
              error={methodsQ.error}
              onRetry={() => void methodsQ.refetch()}
            />
          ) : methodsQ.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : methods.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No payout methods configured. Contact the administrator.
            </p>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <label className="text-sm font-medium" htmlFor="payout-amount">
                    Amount (₱)
                  </label>
                  <MoneyInput
                    id="payout-amount"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">Method</label>
                  <Select value={method} onValueChange={setMethod}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select…" />
                    </SelectTrigger>
                    <SelectContent>
                      {methods.map((m) => (
                        <SelectItem key={m.code} value={m.code}>
                          {m.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {selectedMethod && (
                <div className="space-y-1">
                  <label className="text-sm font-medium" htmlFor="payout-account">
                    {selectedMethod.accountLabel}
                  </label>
                  <Input
                    id="payout-account"
                    value={account}
                    onChange={(e) => setAccount(e.target.value)}
                    placeholder={selectedMethod.accountPlaceholder}
                  />
                </div>
              )}
              {preview && (
                <div className="rounded border p-3 text-sm space-y-1 bg-muted/30">
                  {/* Follow the server's applied sequence — the tenant's global
                      deduction order governs creator cash-outs too. */}
                  {preview.steps && preview.steps.length > 0 ? (
                    preview.steps.map((s, i) => (
                      <Row key={`${s.key}-${i}`} label={s.label} value={s.amount} />
                    ))
                  ) : (
                    <>
                      <Row label="Service charge" value={preview.serviceCharge} />
                      <Row label="Admin fee" value={preview.adminFee} />
                      <Row label="Withholding tax" value={preview.withholdingTax} />
                    </>
                  )}
                  <div className="flex justify-between font-semibold border-t pt-1">
                    <span>You receive</span>
                    <span className="tabular-nums">₱{Number(preview.net).toFixed(2)}</span>
                  </div>
                </div>
              )}
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button
                disabled={!amount || !method || !account.trim() || submit.isPending}
                onClick={() => submit.mutate()}
              >
                {submit.isPending ? 'Submitting…' : 'Request payout'}
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>History</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {requestsQ.isError ? (
            <div className="p-4">
              <QueryError
                what="your payout history"
                error={requestsQ.error}
                onRetry={() => void requestsQ.refetch()}
              />
            </div>
          ) : requests.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground text-sm">
              {requestsQ.isLoading ? 'Loading…' : 'No requests yet.'}
            </p>
          ) : (
            <TableScroll>
              <table className="w-full text-sm">
                <thead className="border-b text-left text-muted-foreground">
                  <tr>
                    <th className="p-3">Amount</th>
                    <th className="p-3">Net</th>
                    <th className="p-3">Method</th>
                    <th className="p-3">Status</th>
                    <th className="p-3" />
                  </tr>
                </thead>
                <tbody>
                  {requests.map((r) => (
                    <tr key={r.id} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="p-3 tabular-nums">₱{Number(r.amount).toFixed(2)}</td>
                      <td className="p-3 tabular-nums">₱{Number(r.netAmount).toFixed(2)}</td>
                      <td className="p-3">{methodLabel(r.method)}</td>
                      <td className="p-3">
                        <Badge variant={payoutStatusVariant(r.status)} className="capitalize">
                          {r.status}
                        </Badge>
                        {r.status === 'rejected' && r.rejectionReason && (
                          <p className="mt-1 max-w-[16rem] text-xs text-muted-foreground">
                            {r.rejectionReason}
                          </p>
                        )}
                      </td>
                      <td className="p-3 text-right">
                        {r.status === 'requested' && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={cancel.isPending}
                            onClick={() => cancel.mutate(r.id)}
                          >
                            Cancel
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-muted-foreground">
      <span>{label}</span>
      <span className="tabular-nums">₱{Number(value).toFixed(2)}</span>
    </div>
  );
}

function payoutStatusVariant(status: string): 'success' | 'destructive' | 'muted' | 'secondary' {
  switch (status) {
    case 'paid':
      return 'success';
    case 'rejected':
      return 'destructive';
    case 'cancelled':
      return 'muted';
    default:
      return 'secondary';
  }
}
